import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import {
  Dialog,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from "@mui/material";
import Button from "components/common/Button";
import GoogleAddress from "components/common/GoogleAddress";
import Input from "components/common/Input";
import TextArea from "components/common/TextAreawithLabel";
import PaymentPage from "./PaymentPage";
import api from "config/api";
import {
  addDays,
  addMinutes,
  format,
  isSameDay,
  setHours,
  setMinutes,
  subMinutes,
} from "date-fns";
import { useAppDispatch, useAppSelector } from "hooks/store";
import moment from "moment";
import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { toast } from "react-toastify";
import {
  placeOrder,
  setGestData,
  setdeliveryType,
  setIsGuestUser,
  setUserData,
  updateCheckoutData,
} from "store/slices/main.slice";
import { convertToTimezone } from "utils";
import { ORDER, ORDER_NAME, TIME_ZONES } from "utils/constants";

type Props = {
  setStage: (v: string) => void;
  setShowPopup: (v: boolean) => void;
  setOrderTotal: (v: number) => void;
};

const formatGlobalTime = (timeZone: string) => {
  const formattedTime = convertToTimezone(new Date(), TIME_ZONES[timeZone]);
  console.log("🚀 - useEffect - formattedTime:", formattedTime);

  if (!formattedTime) {
    throw new Error("Formatted time is undefined or invalid.");
  }

  const [date, time] = formattedTime.split(", ");
  const [mon, day, year] = date.split("/");
  let [hr, min, sec]: string[] | number[] = time.split(":");

  const isPM = time.includes("PM");
  hr = isPM ? (hr === "12" ? 12 : +hr + 12) : hr === "12" ? 0 : +hr;
  sec = sec ? sec.replace(/ AM| PM/, "") : "0";

  const currentTime = new Date();
  currentTime.setMonth(+mon - 1);
  currentTime.setDate(+day);
  currentTime.setFullYear(+year);
  currentTime.setHours(+hr, +min, +sec);
  return currentTime;
};

const initialData = {
  address: "",
  tip: 0,
  clientId: 0,
  suitNo: "",
  buzzerNo: "",
  roomNo: "",
  firstName: "",
  lastName: "",
  address1: "",
  phoneNo: "",
  email: "",
  password: "",
  confirmPassword: "",
  onDate: format(new Date(), "yyyy-MM-dd"),
  onTime: format(new Date(), "HH:mm"),
  selectedAddress: "home",
};

const orderLable: any = {
  TakeOut: ORDER_NAME.TakeOut,
  Delivery: ORDER_NAME.Delivery,
};

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const SLOT_MINUTES = 15;
const CHECKOUT_FONT_FAMILY =
  "Manrope, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'";

const joinAddressParts = (...parts: Array<string | null | undefined>) =>
  parts
    .map((v) => String(v || "").trim().replace(/(^,|,$)/g, ""))
    .filter(Boolean)
    .join(", ");

const roundUpToSlot = (date: Date, stepMinutes = SLOT_MINUTES) => {
  const next = new Date(date);
  next.setSeconds(0, 0);
  const remainder = next.getMinutes() % stepMinutes;
  if (remainder !== 0) {
    next.setMinutes(next.getMinutes() + (stepMinutes - remainder));
  }
  return next;
};

const formatPhoneDisplay = (value: string | number | null | undefined) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return String(value || "");
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const getCartItemImage = (item: any): string => {
  return (
    item?.imageUrl ||
    item?.image ||
    item?.productImage ||
    item?.product?.imageUrl ||
    item?.product?.image ||
    item?.pizza?.imageUrl ||
    item?.pizza?.image ||
    item?.data?.imageUrl ||
    item?.data?.image ||
    ""
  );
};

const getOrderSummaryMeta = (item: any) => {
  if (item?.data?.selectedDealPizzas?.length) {
    return item.data.selectedDealPizzas
      .map((dealPizza: any) => dealPizza?.pizzaName ?? dealPizza?.fullDescription)
      .filter(Boolean)
      .join(", ");
  }

  if (item?.isPizza) {
    return [
      item?.pizzaSize?.size || "",
      item?.spiceLevel?.spicename || "",
      item?.pizzaCrust?.crustName || "",
    ]
      .filter(Boolean)
      .join(", ");
  }

  if (item?.selectedItemsLevel?.length) {
    return item.selectedItemsLevel.map((entry: any) => entry?.name).filter(Boolean).join(", ");
  }

  if (item?.modifiers?.length) {
    return item.modifiers
      .map((modifier: any) => modifier?.description)
      .filter(Boolean)
      .join(", ");
  }

  return item?.note || "";
};

const formatShortDateLabel = (value: string) => {
  const [year, month, day] = String(value || "").split("-");
  if (!year || !month || !day) return value;
  return `${Number(month)}/${Number(day)}`;
};

const normalizeText = (value: string | number | null | undefined) =>
  String(value || "").trim().toLowerCase();

const getApiErrorMessage = (error: any, fallback = "Something went wrong") => {
  const responseData = error?.response?.data;
  const status = error?.response?.status;
  const requestUrl = error?.config?.url || error?.request?.responseURL || "";

  if (status === 404) {
    return `API route not found (404): ${requestUrl || "requested endpoint"}`;
  }

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }

  if (typeof responseData?.title === "string" && responseData.title.trim()) {
    return responseData.title;
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const buildRegisteredCustomerPayload = (customer: any, formData: any) => ({
  ...customer,
  firstName: formData.firstName,
  lastName: formData.lastName,
  email: formData.email,
  phoneNo: String(formData.phoneNo || customer?.phoneNo || "")
    .replace(/\D/g, "")
    .slice(0, 10),
  password: formData.password,
  address: formData.address,
  address1: formData.address1,
  cust_IsGuest: 0,
});

const Details = ({ setStage, setShowPopup, setOrderTotal }: Props) => {
  const [loading, setLoading] = useState(false);
  const [tip, setTip] = useState(0);
  const [notes, setNotes] = useState("");
  const [selectedPayment, setSelectedPayment] = useState("");
  const [authMode, setAuthMode] = useState<"guest" | "login" | "register">("guest");
  const [isLoginVerified, setIsLoginVerified] = useState(false);
  const dispatch = useAppDispatch();
  const [data, setData] = useState(initialData);
  const [isDisableButtons, setIsDisableButtons] = useState<{
    Delivery: boolean;
    TakeOut: boolean;
    Both: boolean;
  }>({ Delivery: false, TakeOut: false, Both: false });
  const [isFetchingRestDates, setIsFetchingRestDates] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [slotPickerDate, setSlotPickerDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const slotDayRowRef = useRef<HTMLDivElement | null>(null);
  const slotDayButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchTrackingRef = useRef(false);
  const customTipInputRef = useRef<HTMLInputElement | null>(null);
  const slotDragStartXRef = useRef(0);
  const slotDragScrollLeftRef = useRef(0);
  const slotIsDraggingRef = useRef(false);

  const {
    userData,
    checkoutData,
    validTimes,
    settings,
    companyData,
    gestData,
    cart,
  } = useAppSelector(({ main }) => main);
  const activeOrderType = checkoutData?.orderType || ORDER.TAKE_OUT;
  const orderSummaryQty = (cart || []).reduce(
    (total: number, item: any) => total + (Number(item?.quantity ?? 1) || 1),
    0
  );

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // Helper: Always prefer logged-in user details. Only fallback to guest details if no userData.
  const preferredProfileData = useMemo(() => {
    return userData ? userData : gestData ? gestData : null;
  }, [userData, gestData]);

  // Helper: Safe display value for labels (prevents showing "undefined")
  const dcsSafe = (v: any) => (v === null || v === undefined ? "" : String(v));
  // === END SAMPATH ===

  const contactSectionTitle = "Contact and payment";
  const zonedNow = useMemo(
    () => (companyData?.timeZone ? formatGlobalTime(companyData.timeZone) : new Date()),
    [companyData?.timeZone]
  );

  const pickupLocationText = useMemo(
    () => joinAddressParts(companyData?.address1, companyData?.address2),
    [companyData]
  );

  const getTimeConfigForDate = (
    orderType: string,
    dateValue: string
  ) => {
    const day = format(new Date(`${dateValue}T00:00:00`), "EEEE");
    return validTimes.find(
      (item: any) => item.timDay === day && item.timOrdtype === orderType
    );
  };

  const isOrderTypeEnabledForDate = (orderType: string, dateValue: string) => {
    const settingClosed =
      orderType === ORDER.TAKE_OUT ? !!settings?.ClosePickupOrder : !!settings?.CloseDeliveryOrder;
    if (settingClosed) return false;

    const found = getTimeConfigForDate(orderType, dateValue);
    if (!found) return true;
    return found.timEnable !== 0;
  };

  const isPickupDisabled = !!settings?.ClosePickupOrder;
  const isDeliveryDisabled = !!settings?.CloseDeliveryOrder;

  const allowPayAtCounter = useMemo(() => {
    if (checkoutData?.orderType === ORDER.TAKE_OUT) {
      return !settings.DontPayAtStorePickup;
    }
    if (checkoutData?.orderType === ORDER.DELIVERY) {
      return !settings.DontPayAtStoreDelivery;
    }
    return true;
  }, [checkoutData?.orderType, settings]);

  const allowOnlinePayment = useMemo(() => {
    if (activeOrderType === ORDER.TAKE_OUT) {
      return !!settings.PickupOnlinePayment;
    }
    if (activeOrderType === ORDER.DELIVERY) {
      return !!settings.DeliveryOnlineOrder;
    }
    return true;
  }, [activeOrderType, settings]);

  const allowPaypalPayment = useMemo(() => {
    const key = activeOrderType === ORDER.DELIVERY ? "PaypalDelivery" : "PaypalPickup";
    return Number((settings as any)?.[key]) === 1;
  }, [activeOrderType, settings]);

  const allowCloverPayment = useMemo(() => {
    const key = activeOrderType === ORDER.DELIVERY ? "CloverDelivery" : "CloverPickup";
    return Number((settings as any)?.[key]) === 1;
  }, [activeOrderType, settings]);

  const deliveryCharge = useMemo(() => {
    if (checkoutData?.orderType !== ORDER.DELIVERY) return 0;
    return companyData?.deliveryCharge || 0;
  }, [checkoutData?.orderType, companyData?.deliveryCharge]);

  const paymentOptions = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: "counter" | "online";
      icon: JSX.Element;
      description: string;
    }> = [];

    if (allowPayAtCounter) {
      items.push({
        id: "oncounter",
        label: "Pay at Counter",
        kind: "counter",
        icon: <StorefrontOutlinedIcon className="!text-[20px]" />,
        description: "Pay when you arrive at the store.",
      });
    }

    if (allowOnlinePayment && allowPaypalPayment) {
      items.push({
        id: "pay_pal",
        label: "PayPal",
        kind: "online",
        icon: <AccountBalanceWalletOutlinedIcon className="!text-[20px]" />,
        description: "Continue to PayPal secure payment.",
      });
    }

    if (allowOnlinePayment && allowCloverPayment) {
      items.push({
        id: "clover",
        label: "Credit or Debit Card",
        kind: "online",
        icon: <CreditCardOutlinedIcon className="!text-[20px]" />,
        description: "Continue to card payment.",
      });
    }

    return items;
  }, [allowCloverPayment, allowOnlinePayment, allowPayAtCounter, allowPaypalPayment]);

  const selectedPaymentOption = useMemo(
    () => paymentOptions.find((option) => option.id === selectedPayment),
    [paymentOptions, selectedPayment]
  );
  const isDifferentCustomerDetails = useMemo(() => {
    if (!userData?.clientId) return false;

    const currentPhone = String(userData?.phoneNo || "").replace(/\D/g, "").slice(0, 10);
    const editedPhone = String(data.phoneNo || "").replace(/\D/g, "").slice(0, 10);

    return (
      normalizeText(userData?.firstName) !== normalizeText(data.firstName) ||
      normalizeText(userData?.lastName) !== normalizeText(data.lastName) ||
      normalizeText(userData?.email) !== normalizeText(data.email) ||
      currentPhone !== editedPhone
    );
  }, [data.email, data.firstName, data.lastName, data.phoneNo, userData]);

  useEffect(() => {
    if (selectedPayment && !paymentOptions.some((option) => option.id === selectedPayment)) {
      setSelectedPayment("");
    }
  }, [paymentOptions, selectedPayment]);

  const loginUser = (params: { emailId: string; password: string }) => {
    const { onDate, onTime } = data;
    setLoading(true);

    api(`/api/Customer/ByIdPass?${new URLSearchParams(params)}`)
      .then(({ data: customer }) => {
        const mergedData = {
          ...customer,
          onDate,
          onTime,
          selectedAddress: data.selectedAddress,
        };

        dispatch(setUserData(customer));
        dispatch(setGestData(undefined));
        dispatch(setIsGuestUser(false));
        dispatch(updateCheckoutData(mergedData));
        setData((prev) => ({ ...prev, ...mergedData }));
        setAuthMode("guest");
        setIsLoginVerified(true);
        toast.success("Login successfully");
      })
      .catch(() => {
        toast.error("Invalid Email or Password");
      })
      .finally(() => setLoading(false));
  };

  const validateRegisterForm = () => {
    if (!data.firstName || !data.lastName) {
      toast.error("First Name & Last Name are required");
      return false;
    }

    if (!/^\d{10}$/.test(data.phoneNo)) {
      toast.error("Invalid Phone Number");
      return false;
    }

    if (!EMAIL_REGEX.test(data.email)) {
      toast.error("Invalid Email");
      return false;
    }

    if (activeOrderType === ORDER.DELIVERY && !data?.address) {
      toast.error("Address is Required");
      return false;
    }

    if (data.password.length <= 3) {
      toast.error("Password must be greater than 3 characters");
      return false;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Confirm password does not match");
      return false;
    }

    return true;
  };

  const registerUser = () => {
    const { onDate, onTime } = data;
    setLoading(true);

    api
      .post(`/api/Customer`, data)
      .then(({ data: customer }) => {
        const mergedData = {
          ...customer,
          onDate,
          onTime,
          address: data.address,
          address1: data.address1,
          selectedAddress: data.selectedAddress,
        };

        dispatch(setUserData(mergedData));
        dispatch(setGestData(undefined));
        dispatch(setIsGuestUser(false));
        dispatch(updateCheckoutData(mergedData));
        setData((prev) => ({ ...prev, ...mergedData }));
        setAuthMode("guest");
        setIsLoginVerified(false);
        toast.success("User created successfully");
      })
      .catch((e) => {
        toast.error(getApiErrorMessage(e));
      })
      .finally(() => setLoading(false));
  };

  const upgradeGuestToRegisteredUser = (customer: any) => {
    const { onDate, onTime } = data;
    const updatedCustomer = buildRegisteredCustomerPayload(customer, data);

    setLoading(true);
    api
      .post("/api/Customer/UpdateCustomerData", updatedCustomer)
      .then(() => {
        const mergedData = {
          ...updatedCustomer,
          onDate,
          onTime,
          selectedAddress: data.selectedAddress,
        };

        dispatch(setUserData(mergedData));
        dispatch(setGestData(undefined));
        dispatch(setIsGuestUser(false));
        dispatch(updateCheckoutData(mergedData));
        setData((prev) => ({ ...prev, ...mergedData }));
        setAuthMode("guest");
        setIsLoginVerified(false);
        toast.success("Account created successfully");
      })
      .catch((e) => {
        toast.error(getApiErrorMessage(e, "Something went wrong while upgrading the guest account"));
      })
      .finally(() => setLoading(false));
  };

  const verifyOrRegisterUser = () => {
    if (!validateRegisterForm()) {
      return;
    }

    setLoading(true);
    api
      .get(`/api/Customer/ByPhone?phoneNo=${data.phoneNo}`)
      .then(({ data: customer }) => {
        setLoading(false);
        upgradeGuestToRegisteredUser(customer);
      })
      .catch(() => {
        setLoading(false);
        registerUser();
      });
  };

  const resolveEditedCustomerAndContinue = (onDate: string, onTime: string) => {
    dispatch(setIsGuestUser(true));
    setLoading(true);

    api
      .get(`/api/Customer/ByPhone?phoneNo=${data.phoneNo}`)
      .then(({ data: customer }) => {
        const updatedCustomer = {
          ...customer,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phoneNo: data.phoneNo,
          address: data.address,
          address1: data.address1,
        };
        const mergedData = {
          ...updatedCustomer,
          onDate,
          onTime,
          address: data.address,
          address1: data.address1,
          selectedAddress: data.selectedAddress,
        };

        api
          .post("/api/Customer/UpdateCustomerData", updatedCustomer)
          .then(() => {
            dispatch(setGestData(mergedData));
            setData((e) => ({ ...e, ...mergedData }));
            toast.success("Customer verified successfully");
            proceedWithCheckout(mergedData);
          })
          .catch((e) => {
            toast.error(getApiErrorMessage(e, "Something went wrong while saving customer details"));
          });
      })
      .catch(() => {
        api
          .post(`api/Customer`, { ...data, password: "123" })
          .then(({ data: customer }) => {
            const mergedData = {
              ...customer,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phoneNo: data.phoneNo,
              onDate,
              onTime,
              address: data.address,
              address1: data.address1,
              selectedAddress: data.selectedAddress,
            };

            setData((e) => ({ ...e, ...mergedData }));
            dispatch(setGestData(mergedData));
            toast.success("Customer verified successfully");
            proceedWithCheckout(mergedData);
            })
          .catch((e) => toast.error(getApiErrorMessage(e)))
          .finally(() => setLoading(false));
      })
      .finally(() => setLoading(false));
  };

  const handleCheckoutSignOut = () => {
    dispatch(setUserData(undefined));
    dispatch(setGestData(undefined));
    dispatch(setIsGuestUser(true));
    setAuthMode("guest");
    setIsLoginVerified(false);
    setData((prev) => ({
      ...initialData,
      onDate: prev.onDate,
      onTime: prev.onTime,
      selectedAddress: prev.selectedAddress,
    }));
  };

  useEffect(() => {
    setIsLoginVerified(false);
  }, [data.email, data.password]);

  const availableDateOptions = useMemo(() => {
    const daysToShow = settings.AllowFutureDate ? 14 : 1;
    return Array.from({ length: daysToShow }, (_, index) => {
      const dateValue = addDays(zonedNow, index);
      const value = format(dateValue, "yyyy-MM-dd");
      return {
        value,
        dayLabel: index === 0 ? "TODAY" : index === 1 ? "TMR" : format(dateValue, "EEE"),
        dateLabel: format(dateValue, "d"),
        disabled: !isOrderTypeEnabledForDate(activeOrderType, value),
      };
    });
  }, [activeOrderType, settings.AllowFutureDate, zonedNow]);

  const getAvailableSlotsForDate = (dateValue: string, orderType: string) => {
    if (!dateValue) return [] as Array<{ value: string; label: string }>;

    const selectedDate = new Date(`${dateValue}T00:00:00`);
    const day = format(selectedDate, "EEEE");
    const found = validTimes.find(
      (i: any) => i.timDay === day && i.timOrdtype === orderType
    );

    if (!found || found?.timEnable === 0 || found?.timIs24Hr) return [];

    const leadMinutes =
      (orderType === ORDER.DELIVERY ? companyData?.deliveryTime : companyData?.pickUpTime) || 0;

    const now = zonedNow;
    const todayEarliest = roundUpToSlot(addMinutes(now, leadMinutes));
    const windows = [
      found.timFrom && found.timTo
        ? {
            from: addMinutes(new Date(found.timFrom), leadMinutes),
            to: subMinutes(new Date(found.timTo), leadMinutes),
          }
        : null,
      found.timFrom1 && found.timTo1
        ? {
            from: addMinutes(new Date(found.timFrom1), leadMinutes),
            to: subMinutes(new Date(found.timTo1), leadMinutes),
          }
        : null,
      found.timFrom2 && found.timTo2
        ? {
            from: addMinutes(new Date(found.timFrom2), leadMinutes),
            to: subMinutes(new Date(found.timTo2), leadMinutes),
          }
        : null,
    ].filter(Boolean) as Array<{ from: Date; to: Date }>;

    return windows.flatMap((window) => {
      let cursor = new Date(window.from);
      cursor = setHours(setMinutes(selectedDate, cursor.getMinutes()), cursor.getHours());

      let end = new Date(window.to);
      end = setHours(setMinutes(selectedDate, end.getMinutes()), end.getHours());

      if (isSameDay(selectedDate, now) && todayEarliest > cursor) {
        cursor = new Date(todayEarliest);
      }

      cursor = roundUpToSlot(cursor);
      const slots: Array<{ value: string; label: string }> = [];

      while (addMinutes(cursor, SLOT_MINUTES) <= end) {
        const slotEnd = addMinutes(cursor, SLOT_MINUTES);
        slots.push({
          value: format(cursor, "HH:mm"),
          label: `${format(cursor, "h:mm aa")}-${format(slotEnd, "h:mm aa")}`,
        });
        cursor = addMinutes(cursor, SLOT_MINUTES);
      }

      return slots;
    });
  };


  const availableSlots = useMemo(() => {
    if (!checkoutData?.orderType || !slotPickerDate) return [];
    return getAvailableSlotsForDate(slotPickerDate, activeOrderType);
  }, [checkoutData?.orderType, activeOrderType, slotPickerDate, validTimes, companyData, zonedNow]);

  const scheduleLabel = useMemo(() => {
    if (!data.onDate || !data.onTime) return "";

    const selectedSlot = availableSlots.find((slot) => slot.value === data.onTime);
    if (selectedSlot && data.onDate === slotPickerDate) {
      return `${formatShortDateLabel(data.onDate)} ${selectedSlot.label}`;
    }

    try {
      const slotStart = new Date(`${data.onDate}T${data.onTime}`);
      const slotEnd = addMinutes(slotStart, SLOT_MINUTES);
      return `${formatShortDateLabel(data.onDate)} ${format(slotStart, "h:mm aa")}-${format(
        slotEnd,
        "h:mm aa"
      )}`;
    } catch {
      return `${data.onDate} ${data.onTime}`;
    }
  }, [availableSlots, data.onDate, data.onTime, slotPickerDate]);

  const standardSchedule = useMemo(() => {
    const leadMinutes =
      (activeOrderType === ORDER.DELIVERY
        ? companyData?.deliveryTime
        : companyData?.pickUpTime) || 0;
    const start = roundUpToSlot(addMinutes(zonedNow, leadMinutes));

    return {
      onDate: format(start, "yyyy-MM-dd"),
      onTime: format(start, "HH:mm"),
      etaLabel: `${leadMinutes}-${leadMinutes + 10} min`,
    };
  }, [activeOrderType, companyData?.deliveryTime, companyData?.pickUpTime, zonedNow]);

  const getStandardScheduleForOrderType = (orderType: string) => {
    const leadMinutes =
      (orderType === ORDER.DELIVERY ? companyData?.deliveryTime : companyData?.pickUpTime) || 0;
    const start = roundUpToSlot(addMinutes(zonedNow, leadMinutes));

    return {
      onDate: format(start, "yyyy-MM-dd"),
      onTime: format(start, "HH:mm"),
    };
  };

  const isStandardDisabled = useMemo(
    () => !isOrderTypeEnabledForDate(activeOrderType, standardSchedule.onDate),
    [activeOrderType, standardSchedule.onDate]
  );

  const isStandardSelection = useMemo(
    () =>
      !isStandardDisabled &&
      data.onDate === standardSchedule.onDate &&
      data.onTime === standardSchedule.onTime,
    [data.onDate, data.onTime, isStandardDisabled, standardSchedule.onDate, standardSchedule.onTime]
  );

  const applyStandardSchedule = () => {
    if (isStandardDisabled) return;

    const nextSchedule = {
      onDate: standardSchedule.onDate,
      onTime: standardSchedule.onTime,
    };

    setData((prev) => ({
      ...prev,
      ...nextSchedule,
    }));
    dispatch(updateCheckoutData(nextSchedule));
  };

  useEffect(() => {
    if (!data.onDate) return;
    if (isOrderTypeEnabledForDate(activeOrderType, data.onDate)) return;

    const nextAvailableDay = availableDateOptions.find((option) => {
      if (option.disabled) return false;
      return getAvailableSlotsForDate(option.value, activeOrderType).length > 0;
    });

    if (!nextAvailableDay) return;

    const nextSlots = getAvailableSlotsForDate(nextAvailableDay.value, activeOrderType);
    const firstSlot = nextSlots[0];
    if (!firstSlot) return;

    const nextSchedule = {
      onDate: nextAvailableDay.value,
      onTime: firstSlot.value,
    };

    setSlotPickerDate(nextAvailableDay.value);
    setData((prev) => ({ ...prev, ...nextSchedule }));
    dispatch(updateCheckoutData(nextSchedule));
  }, [activeOrderType, availableDateOptions, data.onDate, dispatch]);

  const handleSlotDayWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const node = slotDayRowRef.current;
    if (!node) return;

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      node.scrollLeft += event.deltaY;
    }
  };

  const handleSlotDayMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const node = slotDayRowRef.current;
    if (!node) return;

    slotIsDraggingRef.current = true;
    slotDragStartXRef.current = event.clientX;
    slotDragScrollLeftRef.current = node.scrollLeft;
  };

  const handleSlotDayMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const node = slotDayRowRef.current;
    if (!node || !slotIsDraggingRef.current) return;

    const distance = event.clientX - slotDragStartXRef.current;
    if (Math.abs(distance) > 4) {
      node.scrollLeft = slotDragScrollLeftRef.current - distance;
    }
  };

  const stopSlotDayDrag = () => {
    slotIsDraggingRef.current = false;
  };

  useEffect(() => {
    if (!isScheduleDialogOpen) return;

    const timer = window.setTimeout(() => {
      const container = slotDayRowRef.current;
      const selectedButton = slotDayButtonRefs.current[slotPickerDate];
      if (!container || !selectedButton) return;

      const targetLeft =
        selectedButton.offsetLeft -
        container.clientWidth / 2 +
        selectedButton.clientWidth / 2;

      container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "smooth",
      });
    }, 60);

    return () => window.clearTimeout(timer);
  }, [isScheduleDialogOpen, slotPickerDate]);

  // === ADDED BY SAMPATH – 2026-01-06 ===
  // Premium, single-border input styling + consistent label size (fixes "too big label" + removes double borders)
  // + Center Continue button on any device
  // + FIX: Google Places dropdown not visible inside modal (z-index + style for .pac-container)
  const UI_POLISH_CSS = `
    .dcs-details-ui{
      background: #fff;
      font-family: ${CHECKOUT_FONT_FAMILY};
      color: #101828;
      height: 100%;
      min-height: 0;
      display:flex;
      flex-direction:column;
      overflow:hidden;
    }

    /* Card feel */
    .dcs-details-card{
      background: #ffffff;
      border-radius: 0;
      box-shadow: none;
      border: 0;
      overflow: hidden;
      height: 100%;
      min-height: 0;
      display:flex;
      flex-direction:column;
    }
    .dcs-scroll-body{
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow-y: hidden;
      overflow-x: hidden;
      padding-bottom: 20px;
    }
    .dcs-scroll-shell{
      flex: 1 1 auto;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
    }
    .dcs-footer{
      position: sticky;
      bottom: 0;
      background: linear-gradient(180deg, rgba(255,255,255,.82), rgba(255,255,255,.98));
      backdrop-filter: blur(14px);
      border-top: 1px solid rgba(16,24,40,.08);
      padding: 14px 20px calc(14px + env(safe-area-inset-bottom));
    }
    .dcs-footer-inner{
      width: 100%;
      max-width: 1680px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: minmax(0, 1.5fr) minmax(340px, 420px);
      gap: 32px;
      align-items: end;
    }
    .dcs-footer-spacer{
      min-width: 0;
    }
    .dcs-page-header{
      min-height: 56px;
      padding: 0 18px 8px;
      border-bottom: 1px solid rgba(16,24,40,.08);
      display:flex;
      align-items:center;
      justify-content:space-between;
      position: relative;
      overflow: hidden;
      backdrop-filter: blur(18px);
      background:
        radial-gradient(circle at top left, rgba(255,255,255,.92), transparent 34%),
        linear-gradient(135deg, rgba(255,255,255,.94), rgba(241,245,249,.84));
    }
    .dcs-page-header::before{
      content: "";
      position: absolute;
      inset: -30% auto auto -10%;
      width: 240px;
      height: 240px;
      background: radial-gradient(circle, rgba(var(--color-primary) / 0.14), transparent 64%);
      filter: blur(8px);
      animation: dcsHeaderFloat 9s ease-in-out infinite;
      pointer-events: none;
    }
    .dcs-page-header::after{
      content: "";
      position: absolute;
      right: 10%;
      top: -70px;
      width: 180px;
      height: 180px;
      background: radial-gradient(circle, rgba(15,23,42,.08), transparent 68%);
      animation: dcsHeaderFloatAlt 11s ease-in-out infinite;
      pointer-events: none;
    }
    .dcs-page-title{
      position: relative;
      z-index: 1;
      font-size: 28px;
      line-height: 1;
      font-weight: 700;
      color: #101828;
      letter-spacing: -.02em;
    }
    @keyframes dcsHeaderFloat {
      0%, 100% { transform: translate3d(0, 0, 0); }
      50% { transform: translate3d(26px, 10px, 0); }
    }
    @keyframes dcsHeaderFloatAlt {
      0%, 100% { transform: translate3d(0, 0, 0); }
      50% { transform: translate3d(-24px, 12px, 0); }
    }

    /* Normalize label size (Email looked OK, Password looked too big) */
    .dcs-details-ui .MuiFormLabel-root,
    .dcs-details-ui .MuiInputLabel-root{
      font-size: 12px !important;
      line-height: 1.2 !important;
      letter-spacing: .2px;
    }

    /* Single smooth outline (prevents “double/triple border” look) */
    .dcs-details-ui .MuiOutlinedInput-root{
      border-radius: 12px !important;
      background: #fff;
    }
    .dcs-details-ui .MuiOutlinedInput-notchedOutline{
      border-width: 1px !important;
      border-color: rgba(16,24,40,.16) !important;
    }

    /* Soft focus glow (premium look) */
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline{
      border-color: rgba(25,118,210,.45) !important;
    }
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused{
      box-shadow: 0 0 0 4px rgba(25,118,210,.12);
    }

    /* Remove any extra border effects from hover */
    .dcs-details-ui .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline{
      border-color: rgba(16,24,40,.28) !important;
    }

    /* Make Radio label text match premium scale */
    .dcs-details-ui .MuiFormControlLabel-label{
      font-size: 13px;
    }

    /* Continue button centered on any device */
    .dcs-continue-wrap{
      display: flex;
      justify-content: flex-start;
      width: 100%;
      padding: 0 16px;
      margin-top: 6px;
    }
    .dcs-continue-btn{
      width: 100%;
      max-width: 360px;
      min-height: 50px;
      border-radius: 20px !important;
      border: 1px solid rgba(255,255,255,.42) !important;
      background:
        linear-gradient(180deg, rgba(var(--color-primary) / 0.96), rgba(var(--color-primary) / 0.84)) !important;
      color: rgb(var(--color-themeSelColor)) !important;
      box-shadow:
        0 10px 24px rgba(16,24,40,.14),
        inset 0 1px 0 rgba(255,255,255,.30) !important;
      text-shadow: 0 1px 1px rgba(0,0,0,.14);
      font-size: 15px !important;
      font-weight: 800 !important;
      letter-spacing: 0 !important;
      backdrop-filter: blur(8px);
    }
    .dcs-continue-btn:hover{
      box-shadow:
        0 12px 28px rgba(16,24,40,.18),
        inset 0 1px 0 rgba(255,255,255,.34) !important;
      filter: saturate(1.02);
    }
    .dcs-continue-btn:disabled{
      text-shadow: none;
    }
    .dcs-layout{
      flex: 1 1 auto;
      display:grid;
      grid-template-columns: minmax(0, 1.5fr) minmax(340px, 420px);
      gap: 32px;
      align-items:start;
      width: 100%;
      min-height: 100%;
    }
    .dcs-main-column{
      min-width: 0;
      min-height: 0;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      padding-right: 6px;
    }
    .dcs-side-card{
      background: #fff;
      border: 1px solid rgba(16,24,40,.08);
      border-radius: 18px;
      box-shadow: 0 12px 28px rgba(16,24,40,.06);
      padding: 18px;
    }
    .dcs-side-stack{
      display: flex;
      flex-direction: column;
      gap: 18px;
      min-height: 0;
      height: 100%;
      overflow: hidden;
    }
    .dcs-side-card-sticky{
      position: sticky;
      top: 16px;
      z-index: 2;
    }
    .dcs-side-section{
      background: #fff;
      border: 1px solid rgba(16,24,40,.08);
      border-radius: 18px;
      box-shadow: 0 12px 28px rgba(16,24,40,.06);
      padding: 18px 16px;
      min-height: 0;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
    }
    .dcs-side-section-title{
      font-size: 16px;
      font-weight: 600;
      color: #101828;
      margin-bottom: 14px;
    }
    .dcs-payment-grid{
      display:flex;
      flex-wrap:wrap;
      gap: 12px;
      align-items: stretch;
    }
    .dcs-payment-card{
      border: 1px solid rgba(16,24,40,.10);
      border-radius: 16px !important;
      padding: 12px 14px;
      background: rgba(255,255,255,.92);
      text-align:left;
      transition: .2s ease;
      width: min(100%, 320px);
      flex: 0 1 280px;
      min-height: 76px;
      display: flex;
      align-items: center;
    }
    .dcs-payment-card-active{
      background: linear-gradient(180deg, rgba(17,24,39,.96), rgba(31,41,55,.96));
      color:#fff;
      border-color: rgba(17,24,39,.92);
      box-shadow: 0 18px 28px rgba(15,23,42,.18);
    }
    .dcs-summary-row{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 14px;
      padding: 10px 0;
      border-bottom: 1px solid rgba(16,24,40,.08);
    }
    .dcs-summary-row:last-child{
      border-bottom: 0;
    }
    .dcs-total-box{
      margin-top: 16px;
      border: 1px solid rgba(16,24,40,.08);
      border-radius: 18px;
      background: #fff;
      box-shadow: 0 10px 24px rgba(0,0,0,.04);
      padding: 14px 16px;
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 16px;
    }
    .dcs-order-notes-card{
      border: 1px solid rgba(16,24,40,.08);
      border-radius: 18px;
      background: #fff;
      box-shadow: 0 10px 24px rgba(16,24,40,.04);
      padding: 12px 14px;
    }
    .dcs-order-notes-card > div{
      background: transparent !important;
      padding: 0 !important;
    }
    .dcs-order-notes-card label{
      margin-bottom: 8px !important;
      color: #101828 !important;
      font-size: 14px !important;
      font-weight: 700 !important;
    }
    .dcs-order-notes-card textarea{
      width: 100%;
      min-height: 84px;
      border: 1px solid rgba(16,24,40,.10) !important;
      border-radius: 14px !important;
      background: rgba(248,250,252,.72) !important;
      box-shadow: none !important;
      resize: vertical;
    }
    .dcs-order-notes-card textarea:focus{
      border-color: rgba(0,0,0,.25) !important;
      box-shadow: 0 0 0 4px rgba(0,0,0,.06) !important;
    }
    .dcs-side-divider{
      margin: 20px 0;
      border-top: 1px solid rgba(16,24,40,.08);
    }
    .dcs-side-notes-card{
      padding: 0;
      border: 0;
      box-shadow: none;
      background: transparent;
    }
    .dcs-order-summary{
      margin-top: 0;
      border-top: 0;
      padding-top: 0;
      min-height: 0;
      flex: 1 1 auto;
      overflow-y: auto;
      overflow-x: hidden;
      padding-right: 6px;
    }
    .dcs-order-summary-header{
      position: sticky;
      top: 0;
      z-index: 4;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 14px;
      padding: 0 0 10px;
      background: linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(255,255,255,.96) 82%, rgba(255,255,255,0) 100%);
      backdrop-filter: blur(8px);
    }
    .dcs-order-summary-list{
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: min-content;
    }
    .dcs-order-summary-scroll-title{
      font-size: 16px;
      font-weight: 600;
      color: #101828;
      margin: 0;
    }
    .dcs-order-summary-count{
      font-size: 12px;
      font-weight: 700;
      color: #475467;
      line-height: 1;
      white-space: nowrap;
      padding: 7px 10px;
      border-radius: 999px;
      background: rgba(16,24,40,.05);
      border: 1px solid rgba(16,24,40,.08);
    }
    .dcs-order-summary-row{
      display: grid;
      grid-template-columns: 26px 52px minmax(0, 1fr) auto;
      align-items: center;
      gap: 10px;
    }
    .dcs-order-summary-qty{
      min-width: 26px;
      font-size: 14px;
      font-weight: 700;
      color: #344054;
      text-align: right;
    }
    .dcs-order-summary-thumb{
      width: 52px;
      height: 52px;
      border-radius: 14px;
      object-fit: cover;
      background: #f2f4f7;
      border: 1px solid rgba(16,24,40,.06);
    }
    .dcs-order-summary-thumb-fallback{
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 700;
      color: #667085;
      background: #f2f4f7;
    }
    .dcs-order-summary-name{
      font-size: 14px;
      font-weight: 600;
      color: #101828;
      text-transform: uppercase;
      line-height: 1.35;
    }
    .dcs-order-summary-meta{
      margin-top: 4px;
      font-size: 12px;
      font-weight: 600;
      color: #667085;
      line-height: 1.45;
      text-transform: uppercase;
    }
    .dcs-order-summary-price{
      font-size: 14px;
      font-weight: 800;
      color: #101828;
      padding-left: 8px;
      white-space: nowrap;
    }
    .dcs-main-column,
    .dcs-order-summary{
      scrollbar-width: thin;
      scrollbar-color: rgba(255,255,255,.95) rgba(248,250,252,.9);
    }
    .dcs-main-column::-webkit-scrollbar,
    .dcs-order-summary::-webkit-scrollbar{
      width: 10px;
    }
    .dcs-main-column::-webkit-scrollbar-track,
    .dcs-order-summary::-webkit-scrollbar-track{
      background: rgba(248,250,252,.92);
      border-radius: 999px;
    }
    .dcs-main-column::-webkit-scrollbar-thumb,
    .dcs-order-summary::-webkit-scrollbar-thumb{
      background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(226,232,240,.98));
      border-radius: 999px;
      border: 2px solid rgba(248,250,252,.96);
    }
    .dcs-main-column::-webkit-scrollbar-thumb:hover,
    .dcs-order-summary::-webkit-scrollbar-thumb:hover{
      background: linear-gradient(180deg, rgba(255,255,255,1), rgba(203,213,225,1));
    }
    @media (max-width: 1024px){
      .dcs-scroll-body{
        overflow-y: auto;
      }
      .dcs-scroll-shell{
        display: block;
        flex: initial;
        min-height: auto;
      }
      .dcs-layout{
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
        min-height: auto;
        flex: initial;
      }
      .dcs-main-column,
      .dcs-side-stack{
        height: auto;
        overflow: visible;
        padding-right: 0;
      }
      .dcs-side-section,
      .dcs-order-summary{
        min-height: auto;
        flex: initial;
      }
      .dcs-order-summary-list{
        min-height: auto;
      }
      .dcs-order-summary{
        overflow: visible;
        padding-right: 0;
      }
      .dcs-order-summary-header{
        position: static;
        padding-bottom: 0;
        background: transparent;
        backdrop-filter: none;
      }
      .dcs-side-card-sticky{
        position: static;
        top: auto;
      }
      .dcs-footer-inner{
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
      }
      .dcs-footer-spacer{
        display:none;
      }
    }
    @media (max-width: 640px){
      .dcs-page-header{
        padding: 0 12px 8px;
      }
      .dcs-page-title{
        font-size: 22px;
      }
      .dcs-order-notes-card{
        padding: 12px;
      }
      .dcs-order-notes-card textarea{
        min-height: 84px;
      }
      .dcs-order-summary-row{
        grid-template-columns: 26px 52px minmax(0, 1fr) auto;
        gap: 12px;
      }
      .dcs-scroll-body{
        padding-bottom: 12px;
      }
      .dcs-footer{
        padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
      }
      .dcs-continue-wrap{
        justify-content: stretch;
        padding: 0;
      }
      .dcs-continue-btn{
        max-width: none;
      }
    }

    /* === ADDED BY SAMPATH – 2026-01-06 ===
       FIX: Google Places suggestions dropdown hidden behind MUI Dialog/backdrop.
       Places dropdown uses .pac-container appended to <body>, so we force high z-index.
    */
    .pac-container{
      z-index: 20000 !important;
      border-radius: 12px !important;
      overflow: hidden !important;
      border: 1px solid rgba(16,24,40,.12) !important;
      box-shadow: 0 14px 30px rgba(16,24,40,.16) !important;
      font-family: inherit !important;
    }
    .pac-item{
      padding: 10px 12px !important;
      cursor: pointer !important;
    }
    .pac-item:hover{
      background: rgba(16,24,40,.04) !important;
    }
    .pac-item, .pac-item span{
      font-size: 13px !important;
      line-height: 1.25 !important;
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       HEADER: Login LEFT, Register + Guest RIGHT (modern look).
       Selected tab = bold black + underline.
    */
    .dcs-auth-top{
      display:flex;
      align-items:center;
      justify-content:flex-start;
      gap: 14px;
      padding: 10px 0px 6px 0px;
    }

    /* Modern subtle "pill" background (no harsh lines) */
    .dcs-auth-shell{
      background: rgba(16,24,40,.04);
      border-radius: 9999px;
      padding: 6px 10px;
      flex-shrink: 0;
    }

    .dcs-auth-copy{
      flex: 1;
      min-width: 0;
    }

    .dcs-auth-left{
      font-size: 13px;
      font-weight: 600;
      color: rgba(16,24,40,.55);
      cursor: pointer;
      user-select:none;
      border-bottom: 2px solid transparent;
      padding: 6px 10px 4px 10px;
      border-radius: 9999px;
    }
    .dcs-auth-right{
      display:flex;
      gap: 10px;
      align-items:center;
    }
    .dcs-auth-link{
      font-size: 13px;
      font-weight: 600;
      color: rgba(16,24,40,.55);
      cursor: pointer;
      user-select:none;
      border-bottom: 2px solid transparent;
      padding: 6px 10px 4px 10px;
      border-radius: 9999px;
    }
    .dcs-auth-active{
      color: #101828 !important;
      font-weight: 800 !important;
      border-bottom-color: #101828 !important;
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       REMOVE EVERY GRID/SECTION BORDER (no header lines, no dividers, no card border, no "ring" outlines).
       (Input borders stay as-is.)
    */
    .dcs-details-card{
      border: 0 !important;
    }
    .dcs-details-ui .border-b{
      border-bottom: 0 !important;
    }
    .dcs-details-ui .border{
      border: 0 !important;
    }
    .dcs-details-ui .ring-1{
      box-shadow: none !important; /* tailwind ring uses box-shadow */
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       TEXT FIELD STYLE like your screenshot:
       - light gray field background
       - softer border (almost invisible)
       - consistent height + padding
       - nice focus ring using theme primary color
    */
    .dcs-details-ui .MuiOutlinedInput-root{
      background: rgba(16,24,40,.04) !important;
      border-radius: 14px !important;
    }
    .dcs-details-ui .MuiOutlinedInput-notchedOutline{
      border-color: rgba(16,24,40,.10) !important;
    }
    .dcs-details-ui .MuiInputBase-input{
      padding: 12px 14px !important;
      font-size: 14px !important;
      line-height: 1.2 !important;
    }
    .dcs-details-ui .MuiInputLabel-root{
      color: rgba(16,24,40,.55) !important;
    }
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused{
      box-shadow: 0 0 0 4px rgba(0,0,0,.06) !important;
    }
    .dcs-details-ui .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline{
      border-color: rgba(0,0,0,.25) !important;
    }

    /* Order type bar bottom margin 10px */
    .dcs-order-type-bar{
      margin-bottom: 10px !important;
    }

    /* === ADDED BY SAMPATH – 2026-01-15 ===
       MASTER DETAILS (Label view, NOT input fields)
       Matches theme-like soft transparency, clean labels.
    */
    .dcs-master-card{
      background: rgba(16,24,40,.035);
      border: 1px solid rgba(16,24,40,.06);
      border-radius: 14px;
      padding: 12px 12px;
    }
    .dcs-master-row{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 14px;
      padding: 8px 2px;
    }
    .dcs-master-row + .dcs-master-row{
      border-top: 1px solid rgba(16,24,40,.06);
    }
    .dcs-master-label{
      font-size: 12px;
      color: rgba(16,24,40,.55);
      font-weight: 600;
      white-space: nowrap;
      padding-top: 2px;
    }
    .dcs-master-value{
      font-size: 14px;
      color: #101828;
      font-weight: 500;
      text-align: right;
      word-break: break-word;
      max-width: 70%;
    }
    .dcs-contact-summary{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 14px;
      border: 1px solid rgba(16,24,40,.08);
      border-radius: 18px;
      padding: 14px 16px;
      background: linear-gradient(180deg, rgba(248,250,252,.9), rgba(255,255,255,1));
      box-shadow: 0 10px 24px rgba(16,24,40,.04);
    }
    .dcs-contact-summary-main{
      display:flex;
      align-items:flex-start;
      gap: 12px;
      min-width: 0;
    }
    .dcs-contact-avatar{
      width: 36px;
      height: 36px;
      border-radius: 999px;
      display:flex;
      align-items:center;
      justify-content:center;
      background: rgba(16,24,40,.05);
      color:#101828;
      flex-shrink: 0;
    }
    .dcs-contact-summary-name{
      font-size: 18px;
      line-height: 1.25;
      font-weight: 800;
      color:#101828;
    }
    .dcs-contact-summary-line{
      font-size: 14px;
      line-height: 1.5;
      color:#344054;
      word-break: break-word;
    }
    .dcs-signout-link{
      border: 1px solid rgba(16,24,40,.10);
      border-radius: 16px;
      background: #fff;
      padding: 8px 14px;
      font-size: 13px;
      font-weight: 700;
      color:#101828;
      flex-shrink: 0;
    }
    .dcs-section{
      display:grid;
      grid-template-columns: minmax(120px, 150px) minmax(0, 1fr);
      gap: 14px;
      padding: 18px 0;
      border-top: 1px solid rgba(16,24,40,.10);
    }
    .dcs-section:first-of-type{
      border-top: 0;
      padding-top: 8px;
    }
    .dcs-section-title{
      font-size: 15px;
      line-height: 1.45;
      font-weight: 600;
      color: #101828;
    }
    .dcs-section-body{
      min-width: 0;
    }
    .dcs-stack{
      display:flex;
      flex-direction:column;
      gap: 10px;
    }
    .dcs-radio-list{
      display:flex;
      flex-direction:column;
      gap: 6px;
    }
    .dcs-soft-note{
      font-size: 13px;
      line-height: 1.65;
      color: #475467;
    }
    .dcs-contact-grid{
      display:grid;
      grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
      gap: 10px;
      align-items:stretch;
    }
    .dcs-login-grid{
      display:grid;
      grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
      gap: 10px;
      align-items:end;
    }
    .dcs-password-verify{
      display:grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 10px;
      align-items:end;
    }
    .dcs-verify-btn{
      min-height: 40px;
      border-radius: 12px !important;
      padding: 0 16px !important;
      font-size: 13px !important;
      font-weight: 600 !important;
      white-space: nowrap;
    }
    .dcs-two-col{
      display:grid;
      grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
      gap: 10px;
    }
    .dcs-pickup-card{
      border: 1px solid rgba(16,24,40,.08);
      border-radius: 16px;
      padding: 14px;
      background: linear-gradient(180deg, rgba(16,24,40,.02), rgba(16,24,40,.04));
    }
    .dcs-pickup-name{
      font-size: 15px;
      font-weight: 700;
      color: #101828;
      margin-bottom: 4px;
    }
    .dcs-phone-field{
      display:flex;
      flex-direction:column;
      gap: 4px;
      min-width: 0;
    }
    .dcs-phone-label,
    .dcs-field-label{
      font-size: 12px;
      font-weight: 700;
      color: #667085;
      margin-left: 2px;
    }
    .dcs-phone-combo{
      display:flex;
      align-items:center;
      min-height: 40px;
      border-radius: 14px;
      border: 1px solid rgba(16,24,40,.10);
      background: rgba(16,24,40,.04);
      overflow:hidden;
    }
    .dcs-country-select{
      width: 116px;
      height: 40px;
      min-height: 40px;
      border-radius: 0;
      border: 0;
      border-right: 1px solid rgba(16,24,40,.10);
      background: transparent;
      padding: 0 12px;
      font-size: 14px;
      color: #101828;
      outline: none;
      flex-shrink: 0;
    }
    .dcs-phone-input{
      flex: 1 1 auto;
      min-width: 0;
      height: 40px;
      border: 0;
      background: transparent;
      padding: 0 14px;
      font-size: 14px;
      color: #101828;
      outline: none;
    }
    .dcs-phone-input::placeholder{
      color: #98a2b3;
    }
    .dcs-phone-combo:focus-within{
      box-shadow: 0 0 0 4px rgba(0,0,0,.06);
      border-color: rgba(0,0,0,.25);
    }
    .dcs-compact-field .MuiOutlinedInput-root{
      min-height: 40px !important;
    }
    .dcs-compact-field .MuiInputLabel-root{
      display: none !important;
    }
    .dcs-compact-field .MuiInputBase-input{
      padding-top: 9px !important;
      padding-bottom: 9px !important;
    }
    .dcs-inline-help{
      margin-top: -4px;
      font-size: 12px;
      color: #667085;
    }
    .dcs-detail-hint{
      display:inline-flex;
      align-items:center;
      gap: 8px;
      font-size: 14px;
      font-weight: 700;
      color:#101828;
    }
    .dcs-edit-btn{
      width: 36px;
      height: 36px;
      border-radius: 999px;
      border: 1px solid rgba(16,24,40,.10);
      background: #fff;
      display:flex;
      align-items:center;
      justify-content:center;
      flex-shrink: 0;
    }
    .dcs-slot-dialog .MuiPaper-root{
      width: min(560px, calc(100vw - 24px));
      border-radius: 20px;
      overflow: hidden;
    }
    .dcs-slot-shell{
      padding: 18px 18px 20px;
      font-family: ${CHECKOUT_FONT_FAMILY};
      background:
        linear-gradient(180deg, rgba(248,250,252,.96) 0%, rgba(255,255,255,1) 140px);
    }
    .dcs-slot-head{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 16px;
    }
    .dcs-slot-title{
      font-size: 19px;
      line-height: 1.2;
      font-weight: 800;
      color: #101828;
    }
    .dcs-slot-copy{
      margin-top: 6px;
      font-size: 13px;
      line-height: 1.5;
      color: #667085;
      max-width: 360px;
    }
    .dcs-slot-close{
      width: 40px;
      height: 40px;
      border-radius: 999px;
      border: 1px solid rgba(16,24,40,.08);
      background: rgba(255,255,255,.88);
      box-shadow: 0 8px 24px rgba(15,23,42,.08);
      flex-shrink: 0;
    }
    .dcs-slot-day-row{
      display:flex;
      gap: 10px;
      margin-top: 14px;
      overflow-x: auto;
      padding: 2px 2px 10px;
      scroll-snap-type: x proximity;
      scrollbar-width: none;
      cursor: grab;
      user-select: none;
    }
    .dcs-slot-day-row:active{
      cursor: grabbing;
    }
    .dcs-slot-day-row::-webkit-scrollbar{
      display:none;
    }
    .dcs-slot-day{
      min-width: 84px;
      border-radius: 22px;
      border: 1px solid rgba(16,24,40,.08);
      background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
      padding: 12px 10px;
      text-align: center;
      font-size: 12px;
      font-weight: 700;
      color: #101828;
      box-shadow: 0 8px 18px rgba(15,23,42,.05);
      scroll-snap-align: start;
    }
    .dcs-slot-day-disabled{
      opacity: .38;
      cursor: not-allowed;
      box-shadow: none;
    }
    .dcs-slot-day-label{
      font-size: 12px;
      letter-spacing: .03em;
      text-transform: uppercase;
      color: #667085;
    }
    .dcs-slot-date-label{
      margin-top: 4px;
      font-size: 18px;
      line-height: 1;
      font-weight: 800;
      color: #101828;
    }
    .dcs-slot-day-active{
      background: linear-gradient(135deg, #111827 0%, #334155 100%);
      border-color: #111827;
      box-shadow: 0 16px 28px rgba(15,23,42,.22);
      transform: translateY(-1px);
    }
    .dcs-slot-day-active .dcs-slot-day-label,
    .dcs-slot-day-active .dcs-slot-date-label{
      color: #fff;
    }
    .dcs-slot-grid{
      display:grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
      margin-top: 14px;
      max-height: 54vh;
      overflow: auto;
      padding-right: 6px;
      scrollbar-width: thin;
      scrollbar-color: rgba(255,255,255,.98) rgba(255,255,255,.72);
    }
    .dcs-slot-grid::-webkit-scrollbar{
      width: 10px;
    }
    .dcs-slot-grid::-webkit-scrollbar-track{
      background: rgba(255,255,255,.82);
      border-radius: 999px;
      box-shadow: inset 0 0 0 1px rgba(16,24,40,.04);
    }
    .dcs-slot-grid::-webkit-scrollbar-thumb{
      background: linear-gradient(180deg, rgba(255,255,255,.98) 0%, rgba(241,245,249,.98) 100%);
      border-radius: 999px;
      border: 1px solid rgba(16,24,40,.08);
    }
    .dcs-slot-grid::-webkit-scrollbar-thumb:hover{
      background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%);
    }
    .dcs-slot-btn{
      min-height: 42px;
      border-radius: 999px;
      border: 1px solid rgba(16,24,40,.08);
      background: rgba(16,24,40,.05);
      padding: 8px 12px;
      font-size: 13px;
      font-weight: 700;
      color: #101828;
      text-align: center;
    }
    .dcs-slot-btn-active{
      background: #1f2937;
      border-color: #1f2937;
      color: #fff;
    }

    @media (max-width: 640px){
      .dcs-section{
        grid-template-columns: 1fr;
        gap: 10px;
      }
      .dcs-layout{
        grid-template-columns: 1fr;
      }
      .dcs-payment-grid{
        display: grid;
        grid-template-columns: 1fr;
      }
      .dcs-payment-card{
        width: 100%;
        flex: initial;
      }
      .dcs-contact-grid{
        grid-template-columns: 1fr;
      }
      .dcs-login-grid{
        grid-template-columns: 1fr;
      }
      .dcs-password-verify{
        grid-template-columns: minmax(0, 1fr) auto;
      }
      .dcs-two-col{
        grid-template-columns: 1fr;
      }
      .dcs-slot-grid{
        grid-template-columns: 1fr;
      }
      .dcs-slot-day{
        min-width: 76px;
      }
      .dcs-page-title{
        font-size: 22px;
      }
      .dcs-auth-top{
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
      }
      .dcs-auth-right{
        justify-content: flex-start;
        flex-wrap: wrap;
      }
      .dcs-contact-summary{
        flex-direction: row;
        align-items: center;
      }
      .dcs-contact-summary-main{
        align-items: center;
      }
      .dcs-signout-link{
        align-self: center;
      }
    }
    @media (max-width: 420px){
      .dcs-country-select{
        width: 104px;
      }
    }
    /* === END SAMPATH === */
  `;
  // === END SAMPATH ===

  useEffect(() => {
    let updatedData = {
      ...initialData,
      onTime: format(formatGlobalTime(companyData?.timeZone!), "HH:mm"),
    };
    const currentTime = format(formatGlobalTime(companyData?.timeZone!), "HH:mm");
    const currentTimeObj = formatGlobalTime(companyData?.timeZone!);

    if (moment(currentTimeObj).isValid()) {
      updatedData.onDate = format(currentTimeObj, "yyyy-MM-dd");
    }

    if (!data.clientId) {
      // === ADDED BY SAMPATH – 2026-01-15 ===
      // Always load logged-in user details first. If no userData, then load guest details.
      if (preferredProfileData) {
        updatedData = { ...updatedData, ...preferredProfileData };
      }
      if (checkoutData) {
        updatedData = {
          ...updatedData,
          onDate: checkoutData.onDate || updatedData.onDate,
          onTime: checkoutData.onTime || updatedData.onTime,
          selectedAddress: checkoutData.selectedAddress || updatedData.selectedAddress,
          address: checkoutData.address ?? updatedData.address,
          address1: checkoutData.address1 ?? updatedData.address1,
        };
      }
      // === END SAMPATH ===

      // (kept original selectedAddress logic)
      if (checkoutData?.selectedAddress) {
        updatedData.selectedAddress = checkoutData?.selectedAddress;
      }

      if (
        format(currentTimeObj, "yyyy-MM-dd") === updatedData.onDate ||
        format(currentTimeObj, "yyyy-MM-dd") > updatedData.onDate
      ) {
        const [hours, minutes] = currentTime.split(":").map(Number);
        currentTimeObj.setHours(hours, minutes);

        let updatedTime: Date | undefined;

        if (companyData && activeOrderType === ORDER.DELIVERY) {
          updatedTime = addMinutes(currentTimeObj, companyData.deliveryTime);
        } else if (companyData && activeOrderType === ORDER.TAKE_OUT) {
          updatedTime = addMinutes(currentTimeObj, companyData.pickUpTime);
        }

        if (updatedTime) {
          const roundedStandardTime = roundUpToSlot(updatedTime as Date);
          const selectedTime: Date = currentTimeObj;
          const [selectedHours, selectedMinutes] = updatedData.onTime
            .split(":")
            .map(Number);
          selectedTime.setHours(selectedHours, selectedMinutes);

          if (selectedTime < roundedStandardTime) {
            updatedData.onDate = format(roundedStandardTime, "yyyy-MM-dd");
            updatedData.onTime = format(roundedStandardTime, "HH:mm");
          }
        }
      }
      setData(updatedData);
    }
  }, [
    companyData,
    checkoutData,
    gestData,
    userData,
    data.clientId,
    // === ADDED BY SAMPATH – 2026-01-15 ===
    preferredProfileData,
    // === END SAMPATH ===
  ]);

  const handleSubmit = async () => {
    toast.dismiss();

    let isTimeValid = true;
    let { onDate, onTime } = data;
    const shouldUseEditedCustomer = !!userData && isDifferentCustomerDetails;

    if (userData || (data && data.clientId)) {
      if (format(new Date(), "yyyy-MM-dd") === data.onDate) {
        let currentTime: Date = new Date();

        const formattedTime = convertToTimezone(
          new Date(),
          TIME_ZONES[companyData?.timeZone!]
        );

        const [date, time] = formattedTime.split(", ");

        const [mon, _, year] = date.split("/");
        console.log("\n _ ----->", _);
        const [hr, min, sec] = time.replace(" AM", "").split(":");

        currentTime.setMonth(+mon - 1);
        currentTime.setFullYear(+year);
        currentTime.setHours(+hr, +min, +sec);

        const formattedTimeInHour = moment().format("HH:mm");

        if (formattedTimeInHour && formattedTimeInHour.includes(":")) {
          const [hours, minutes] = formattedTime.split(":").map(Number);

          if (!isNaN(hours) && !isNaN(minutes)) {
            currentTime.setHours(hours, minutes);
          }
        }

        let updatedTime: Date | undefined;

        if (companyData && activeOrderType === ORDER.DELIVERY) {
          updatedTime = addMinutes(currentTime, companyData.deliveryTime);
        } else if (companyData && activeOrderType === ORDER.TAKE_OUT) {
          updatedTime = addMinutes(currentTime, companyData.pickUpTime);
        }

        if (updatedTime) {
          const selectedTime: Date = new Date();

          const [selectedHours, selectedMinutes] = data.onTime
            .split(":")
            .map(Number);

          selectedTime.setHours(selectedHours, selectedMinutes);

          console.log("selectedTime", selectedHours);

          if (selectedTime < updatedTime) {
            setData({ ...data, onTime: format(updatedTime, "HH:mm") });
            onTime = format(updatedTime, "HH:mm");
            toast.success(
              `Your ${
                activeOrderType === ORDER.DELIVERY ? "delivery" : "takeout"
              } time has been updated to ${format(updatedTime, "HH:mm")}.`
            );

            isTimeValid = true;
          }
        }
      }

      if (isTimeValid) {
        const submissionData = {
          ...data,
          onDate,
          onTime,
        };

        if (shouldUseEditedCustomer) {
          if (!EMAIL_REGEX.test(submissionData.email)) {
            toast.error("Invalid Email");
            return;
          }

          if (!submissionData.firstName || !submissionData.lastName) {
            toast.error("First Name & Last Name are required");
            return;
          }

          if (!/^\d{10}$/.test(submissionData.phoneNo)) {
            toast.error("Invalid Phone Number");
            return;
          }

          if (activeOrderType === ORDER.DELIVERY) {
            if (!submissionData?.address && !submissionData?.address1) {
              toast.error("At least one address is required.");
              return;
            }

            if (!submissionData?.selectedAddress) {
              toast.error("Please select a delivery address.");
              return;
            }

            if (submissionData.selectedAddress === "home" && !submissionData?.address) {
              toast.error("Home address is required.");
              return;
            }

            if (submissionData.selectedAddress === "office" && !submissionData?.address1) {
              toast.error("Office address is required.");
              return;
            }
          }

          resolveEditedCustomerAndContinue(onDate, onTime);
          return;
        }

        if (activeOrderType === ORDER.DELIVERY) {
          if (!submissionData?.address && !submissionData?.address1) {
            toast.error("At least one address is required.");
            return;
          }

          if (!submissionData?.selectedAddress) {
            toast.error("Please select a delivery address.");
            return;
          }

          if (submissionData.selectedAddress === "home" && !submissionData?.address) {
            toast.error("Home address is required.");
            return;
          }

          if (submissionData.selectedAddress === "office" && !submissionData?.address1) {
            toast.error("Office address is required.");
            return;
          }
          if (submissionData.selectedAddress === "home") {
            submissionData.address = submissionData?.address || userData?.address || "";
          } else if (submissionData.selectedAddress === "office") {
            submissionData.address1 =
              submissionData?.address1 || userData?.address1 || "";
          }
        }
        if (userData) {
          const currentUserData = {
            ...userData,
            firstName: submissionData.firstName,
            lastName: submissionData.lastName,
            email: submissionData.email,
            phoneNo: submissionData.phoneNo,
            address: submissionData?.address || "",
            ...(submissionData?.address1 || userData?.address1
              ? { address1: submissionData.address1 || userData.address1 }
              : {}),
          };

          const isAddressSame =
            submissionData?.address === userData?.address &&
            ((submissionData?.address1 &&
              userData?.address1 &&
              submissionData.address1 === userData.address1) ||
              (!submissionData?.address1 && !userData?.address1));
          const isProfileSame =
            normalizeText(submissionData.firstName) === normalizeText(userData?.firstName) &&
            normalizeText(submissionData.lastName) === normalizeText(userData?.lastName) &&
            normalizeText(submissionData.email) === normalizeText(userData?.email) &&
            String(submissionData.phoneNo || "").replace(/\D/g, "") ===
              String(userData?.phoneNo || "").replace(/\D/g, "");

          if (!isAddressSame || !isProfileSame) {
            api
              .post("/api/Customer/UpdateCustomerData", currentUserData)
              .then(() => {
                toast.success("User details updated successfully");
                dispatch(setUserData(structuredClone(currentUserData)));
              })
              .catch((e) => {
                toast.error(getApiErrorMessage(e, "Something went wrong while updating user details"));
              });
          }
        }

        proceedWithCheckout(submissionData);
        return;
      }
    }

    if (!EMAIL_REGEX.test(data.email)) {
      toast.error("Invalid Email");
      return;
    }

    setTimeout(() => {
      if (authMode === "login") {
        if (!data.email || !data.password) {
          toast.error("Please verify your sign in details first");
          return;
        }
        if (!isLoginVerified) {
          toast.error("Please verify your sign in details first");
          return;
        }
      }

      if (authMode === "register") {
        verifyOrRegisterUser();
        return;
      }

      if (!data.firstName || !data.lastName) {
        toast.error("First Name & Last Name are required");
        return;
      }

      if (!/^\d{10}$/.test(data.phoneNo)) {
        toast.error("Invalid Phone Number");
        return;
      }

      if (activeOrderType === ORDER.DELIVERY) {
        if (!data?.address) {
          toast.error("Address is Required");
          return;
        }
      }

      dispatch(setIsGuestUser(true));
      setLoading(true);
      api
        .get(`/api/Customer/ByPhone?phoneNo=${data.phoneNo}`)
        .then(({ data: customer }) => {
          const existingCustomerData = {
            ...customer,
            firstName: data.firstName || customer?.firstName,
            lastName: data.lastName || customer?.lastName,
            email: data.email || customer?.email,
            phoneNo: String(customer?.phoneNo || data.phoneNo)
              .replace(/\D/g, "")
              .slice(0, 10),
            address: data.address,
            address1: data.address1,
          };
          const guestCheckoutData = {
            ...existingCustomerData,
            onDate,
            onTime,
            selectedAddress: data.selectedAddress,
          };

          api
            .post("/api/Customer/UpdateCustomerData", existingCustomerData)
            .then(() => {
              dispatch(setGestData(guestCheckoutData));
              setData((prev) => ({ ...prev, ...guestCheckoutData }));
              toast.success("Existing customer verified successfully");
              proceedWithCheckout(guestCheckoutData);
            })
            .catch((e) => {
              toast.error(getApiErrorMessage(e, "Something went wrong while saving customer details"));
            });
        })
        .catch(() => {
          api
            .post(`api/Customer`, { ...data, password: "123" })
            .then(({ data: customer }) => {
              const guestCheckoutData = {
                ...customer,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNo: data.phoneNo,
                onDate,
                onTime,
                address: data.address,
                address1: data.address1,
                selectedAddress: data.selectedAddress,
              };

              dispatch(setGestData(guestCheckoutData));
              toast.success("Guest details saved successfully");
              proceedWithCheckout(guestCheckoutData);
            })
            .catch((e) => {
              toast.error(getApiErrorMessage(e));
            });
        })
        .finally(() => setLoading(false));
    }, 300);
  };

  const handleMethodChange = (nextOrderType: string) => {
    if (nextOrderType === activeOrderType) return;
    if (nextOrderType === ORDER.TAKE_OUT && isPickupDisabled) return;
    if (nextOrderType === ORDER.DELIVERY && isDeliveryDisabled) return;

    const nextStandardSchedule = getStandardScheduleForOrderType(nextOrderType);
    const shouldUseStandard = isOrderTypeEnabledForDate(nextOrderType, nextStandardSchedule.onDate);
    const nextSchedule = shouldUseStandard
      ? nextStandardSchedule
      : (() => {
          const nextAvailableDay = availableDateOptions.find((option) => {
            if (option.disabled) return false;
            return getAvailableSlotsForDate(option.value, nextOrderType).length > 0;
          });

          if (!nextAvailableDay) return null;

          const firstSlot = getAvailableSlotsForDate(nextAvailableDay.value, nextOrderType)[0];
          if (!firstSlot) return null;

          return {
            onDate: nextAvailableDay.value,
            onTime: firstSlot.value,
          };
        })();

    dispatch(setdeliveryType(nextOrderType === ORDER.TAKE_OUT ? "pickup" : "delivery"));
    if (nextSchedule) {
      setSlotPickerDate(nextSchedule.onDate);
      setData((prev) => ({ ...prev, ...nextSchedule }));
    }
    dispatch(updateCheckoutData({ orderType: nextOrderType, ...(nextSchedule || {}) }));
  };

  useEffect(() => {
    if (activeOrderType === ORDER.TAKE_OUT && settings.ClosePickupOrder && !settings.CloseDeliveryOrder) {
      dispatch(setdeliveryType("delivery"));
      dispatch(updateCheckoutData({ orderType: ORDER.DELIVERY }));
      return;
    }

    if (activeOrderType === ORDER.DELIVERY && settings.CloseDeliveryOrder && !settings.ClosePickupOrder) {
      dispatch(setdeliveryType("pickup"));
      dispatch(updateCheckoutData({ orderType: ORDER.TAKE_OUT }));
    }
  }, [activeOrderType, dispatch, settings.CloseDeliveryOrder, settings.ClosePickupOrder]);

  const proceedWithCheckout = (nextData: any) => {
    if (!selectedPaymentOption) {
      toast.error("Please select a payment method");
      return;
    }

    const updatedCheckout = {
      ...nextData,
      tip,
      notes,
      paymentType: selectedPaymentOption.id,
      total: (checkoutData?.total || 0) + tip + deliveryCharge,
    };

    dispatch(updateCheckoutData(updatedCheckout));

    if (selectedPaymentOption.kind === "counter") {
      dispatch(
        placeOrder({
          tip,
          deliveryCharge,
          extra: deliveryCharge,
          paymentType: "oncounter",
          setStage,
          notes,
          setShowPopup,
          setOrderTotal,
        }) as unknown as any
      );
      return;
    }

    setIsPaymentDialogOpen(true);
  };

  const checkTimeRange = (
    from: Date | undefined,
    to: Date | undefined,
    hour: number,
    min: number
  ) => {
    let error = false;
    if (!from || !to) return false;
    if (
      hour < from.getHours() ||
      (hour > to.getHours() && from.getDate() === to.getDate())
    ) {
      error = true;
    }

    if (hour === from.getHours() && min < from.getMinutes()) {
      error = true;
    }

    if (hour === to.getHours() && min > to.getMinutes()) {
      error = true;
    }
    return !error;
  };

  const timeError = useMemo(() => {
    if (!userData) return "";

    const [hour, min] = data.onTime.split(":");
    const day = format(new Date(data.onDate), "EEEE");

    const found = validTimes.find(
      (i: any) => i.timDay === day && i.timOrdtype === activeOrderType
    );

    if (!found) return "";
    if (found?.timEnable === 0) {
      return `${ORDER_NAME[activeOrderType]} is closed on ${day}`;
    }
    if (found?.timIs24Hr) return "";
    const extraTime =
      (activeOrderType === ORDER.DELIVERY
        ? companyData?.deliveryTime
        : companyData?.pickUpTime) || 0;

    const from = addMinutes(new Date(found.timFrom), extraTime);
    const to = subMinutes(new Date(found.timTo), extraTime);
    const from1 = found.timFrom1
      ? addMinutes(new Date(found.timFrom1), extraTime)
      : undefined;
    const to1 = found.timTo1
      ? subMinutes(new Date(found.timTo1), extraTime)
      : undefined;
    const from2 = found.timFrom2
      ? addMinutes(new Date(found.timFrom2), extraTime)
      : undefined;
    const to2 = found.timTo2
      ? subMinutes(new Date(found.timTo2), extraTime)
      : undefined;

    let error = `${
      ORDER_NAME[activeOrderType]
    } is closed at the moment, you can make this order between `;

    if (from && to) {
      error += `${format(from, "hh:mm aa")} - ${format(to, "hh:mm aa")}`;
    }
    if (from1 && to1) {
      error += `, ${format(from1, "hh:mm aa")} - ${format(to1, "hh:mm aa")}`;
    }
    if (from2 && to2) {
      error += `, ${format(from2, "hh:mm aa")} - ${format(to2, "hh:mm aa")}`;
    }

    error += ` on ${day}`;
    const t = checkTimeRange(from, to, +hour, +min);
    const t1 = checkTimeRange(from1, to1, +hour, +min);
    const t2 = checkTimeRange(from2, to2, +hour, +min);

    if (t || t1 || t2) {
      return "";
    }
    return error;
  }, [
    validTimes,
    data.onTime,
    data.onDate,
    activeOrderType,
    companyData,
    userData,
  ]);

  const isRestDate = useMemo(
    () =>
      (activeOrderType === ORDER.TAKE_OUT && isDisableButtons.TakeOut) ||
      (activeOrderType === ORDER.DELIVERY &&
        isDisableButtons.Delivery) ||
      isDisableButtons.Both,
    [isDisableButtons, activeOrderType]
  );

  const fetchData = async () => {
    setIsFetchingRestDates(true);
    try {
      const { data: restDatesData } = await api.get("api/RestDates/GetRestdates");

      const checkOrderAvailability = (
        orderType: "TakeOut" | "Delivery" | "Both"
      ) => {
        const currentDate = new Date(data.onDate);

        const filteredOrders = restDatesData.filter(
          (item: any) => item.timdOrdtype === orderType
        );

        for (const order of filteredOrders) {
          const fromDate = new Date(order.timdFrom);
          const toDate = new Date(order.timdTo);

          if (currentDate >= fromDate && currentDate <= toDate) {
            return true;
          }
        }

        return false;
      };

      setIsDisableButtons({
        Delivery: checkOrderAvailability("Delivery"),
        TakeOut: checkOrderAvailability("TakeOut"),
        Both: checkOrderAvailability("Both"),
      });
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsFetchingRestDates(false);
      }, 1000);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.onDate]);

  // === ADDED BY SAMPATH – 2026-01-15 ===
  // IMPORTANT FIX:
  // Do NOT let guest data overwrite logged-in user data.
  // Only apply gestData to local form when NO userData and the current tab is Guest.
  useEffect(() => {
    if (!userData && gestData) {
      setData(gestData);
    }
  }, [gestData, userData]);

  useEffect(() => {
    if (data.onDate) {
      setSlotPickerDate(data.onDate);
    }
  }, [data.onDate]);

  useEffect(() => {
    if (checkoutData?.tip && !tip) {
      setTip(checkoutData.tip);
    }
  }, [checkoutData?.tip, tip]);

  useEffect(() => {
    if (!selectedPayment && paymentOptions.length) {
      setSelectedPayment(paymentOptions[0].id);
    }
  }, [paymentOptions, selectedPayment]);

  const updateTip = (v: number) => {
    setTip(v);
    dispatch(updateCheckoutData({ tip: v }));
  };

  const focusCustomTipInput = () => {
    setTimeout(() => {
      customTipInputRef.current?.focus();
      customTipInputRef.current?.select?.();
    }, 0);
  };

  const handleDetailsTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;
    const touch = event.touches[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    touchTrackingRef.current = true;
  };

  const handleDetailsTouchMove = (_event: TouchEvent<HTMLDivElement>) => {
    if (!touchTrackingRef.current) return;
  };

  const handleDetailsTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (!touchTrackingRef.current) return;
    touchTrackingRef.current = false;
    if (typeof window !== "undefined" && window.innerWidth >= 640) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    if (deltaX > 80 && Math.abs(deltaX) > Math.abs(deltaY)) {
      setStage("CART");
    }
  };

  const tip10 = useMemo(
    () => +(((checkoutData?.total || 0) * 10) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const tip15 = useMemo(
    () => +(((checkoutData?.total || 0) * 15) / 100).toFixed(2),
    [checkoutData?.total]
  );
  const tip20 = useMemo(
    () => +(((checkoutData?.total || 0) * 20) / 100).toFixed(2),
    [checkoutData?.total]
  );
  // === END SAMPATH ===

  if (!checkoutData) return <></>;

  return isFetchingRestDates ? (
    <div className="flex justify-center items-center w-full h-auto ">
      <div className="w-10 h-10  border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
    </div>
  ) : (
    <>
      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      <style>{UI_POLISH_CSS}</style>
      {/* === END SAMPATH === */}

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      <div
        className="dcs-details-ui w-full"
        onTouchStart={handleDetailsTouchStart}
        onTouchMove={handleDetailsTouchMove}
        onTouchEnd={handleDetailsTouchEnd}
      >
        <div className="dcs-details-card w-full">
          {/* === END SAMPATH === */}

          <div className="dcs-page-header">
            <div
              className="relative z-[1]"
              onClick={() => {
                setStage("CART");
              }}
            >
              <ArrowBackIosNewIcon className="!text-[15px] !text-primary cursor-pointer" />
            </div>
            <h4 className="dcs-page-title">Checkout Details</h4>
            <div />
          </div>

          {/* === ADDED BY SAMPATH – 2026-01-06 === */}
          {/* === ADDED BY SAMPATH – 2026-01-15 === */}
          {/* Order Type bottom margin 10px */}
          <div className="dcs-order-type-bar flex items-center justify-between px-4 py-2 border-b bg-[rgba(16,24,40,0.03)]">
            {/* === END SAMPATH === */}
            <span className="text-[12px] font-medium text-[#667085] tracking-wide">
              Order Type
            </span>

            <span
              className="
                px-3 py-1
                text-[12px]
                font-semibold
                rounded-full
                bg-[rgba(16,24,40,0.08)]
                text-[#101828]
              "
            >
              {orderLable[activeOrderType]}
            </span>
          </div>
          {/* === END SAMPATH === */}

          <div className="dcs-scroll-body px-3 md:px-6 xl:px-8">
            <div className="dcs-scroll-shell pt-3 w-full max-w-[1680px] mx-auto">
              <div className="dcs-layout">
                <div className="dcs-main-column">
              <div className="dcs-section">
                <div className="dcs-section-title">Method</div>
                <div className="dcs-section-body">
                  <div className="relative inline-grid grid-cols-2 rounded-full bg-[#f3f4f6] p-1 shadow-inner">
                    <span
                      className={`absolute bottom-1 top-1 w-[calc(50%-4px)] rounded-full bg-white shadow-[0_1px_4px_rgba(15,23,42,0.14)] transition-transform duration-300 ease-out ${
                        activeOrderType === ORDER.TAKE_OUT ? "translate-x-full" : ""
                      }`}
                      style={{ left: "4px" }}
                    />
                    <button
                      type="button"
                      disabled={isDeliveryDisabled}
                      className={`relative z-10 rounded-full px-4 py-2 text-[14px] font-[700] transition-colors duration-300 ease-out ${
                        activeOrderType === ORDER.DELIVERY
                          ? "text-[#111827]"
                          : "text-[#111827]"
                      } ${isDeliveryDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      onClick={() => handleMethodChange(ORDER.DELIVERY)}
                    >
                      Delivery
                    </button>
                    <button
                      type="button"
                      disabled={isPickupDisabled}
                      className={`relative z-10 rounded-full px-4 py-2 text-[14px] font-[700] transition-colors duration-300 ease-out ${
                        activeOrderType === ORDER.TAKE_OUT
                          ? "text-[#111827]"
                          : "text-[#111827]"
                      } ${isPickupDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      onClick={() => handleMethodChange(ORDER.TAKE_OUT)}
                    >
                      Pickup
                    </button>
                  </div>
                  <div className="mt-2 text-[12px] font-[500] text-[#667085]">
                    {activeOrderType === ORDER.DELIVERY
                      ? "You have selected Delivery."
                      : "You have selected Pick up."}
                  </div>
                </div>
              </div>

              <div className="dcs-section">
                <div className="dcs-section-title">
                  {activeOrderType === ORDER.DELIVERY ? "Delivery time" : "Pickup time"}
                </div>
                <div className="dcs-section-body">
                  <div className="dcs-stack">
                    <button
                      type="button"
                      disabled={isStandardDisabled}
                      className={`flex w-full items-center gap-3 text-left ${
                        isStandardDisabled ? "opacity-40 cursor-not-allowed" : ""
                      }`}
                      onClick={applyStandardSchedule}
                    >
                      <Radio checked={isStandardSelection} disabled={isStandardDisabled} size="small" />
                      <div className="text-[14px] font-[700] text-[#101828]">
                        Standard · {standardSchedule.etaLabel}
                      </div>
                    </button>

                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-start gap-3 text-left"
                        onClick={() => setIsScheduleDialogOpen(true)}
                      >
                        <Radio checked={!isStandardSelection} size="small" />
                        <div className="min-w-0">
                          <div className="text-[14px] font-[700] text-[#101828]">
                            Schedule for later
                          </div>
                          {!isStandardSelection && !!scheduleLabel && (
                            <div className="mt-1 text-[14px] font-[700] text-[#101828]">
                              {scheduleLabel}
                            </div>
                          )}
                        </div>
                      </button>

                      {!isStandardSelection && (
                        <button
                          type="button"
                          className="dcs-edit-btn"
                          onClick={() => setIsScheduleDialogOpen(true)}
                          aria-label="Edit schedule"
                        >
                          <EditOutlinedIcon className="!text-[18px] !text-[#101828]" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {activeOrderType === ORDER.TAKE_OUT ? (
                <div className="dcs-section">
                  <div className="dcs-section-title">Pickup address</div>
                  <div className="dcs-section-body">
                    <div className="dcs-pickup-card">
                      <div className="dcs-pickup-name">
                        {dcsSafe(companyData?.companyName) || "Pickup location"}
                      </div>
                      <div className="dcs-soft-note">{pickupLocationText}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="dcs-section">
                  <div className="dcs-section-title">Delivery address</div>
                  <div className="dcs-section-body">
                    <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/10 relative pt-3 px-3 pb-2">
                      <p className="absolute left-[12px] -top-[10px] text-[12px] px-2 text-[#475467] bg-white rounded-full ring-1 ring-black/10">
                        Delivery Address
                      </p>

                      <RadioGroup
                        value={data.selectedAddress}
                        onChange={(e) =>
                          setData((prev) => ({
                            ...prev,
                            selectedAddress: e.target.value,
                          }))
                        }
                        name="delivery-address-group"
                      >
                        <div className="flex flex-col justify-center mb-1.5">
                          <FormControlLabel
                            value="home"
                            control={<Radio size="small" />}
                            label="Home Address"
                          />

                          <GoogleAddress
                            className="flex-1 ml-2 [&_.MuiInputBase-root]:min-h-[44px] [&_.MuiInputBase-root]:py-0"
                            value={data.address || ""}
                            onChange={(address) =>
                              setData((prev) => ({ ...prev, address }))
                            }
                          />
                        </div>

                        <div className="flex flex-col justify-center mb-1.5">
                          <FormControlLabel
                            value="office"
                            control={<Radio size="small" />}
                            label="Office Address"
                          />

                          <GoogleAddress
                            className="flex-1 ml-2 [&_.MuiInputBase-root]:min-h-[44px] [&_.MuiInputBase-root]:py-0"
                            value={data.address1 || ""}
                            onChange={(address1) =>
                              setData((prev) => ({ ...prev, address1 }))
                            }
                          />
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>
              )}

              <div className="dcs-section">
                <div className="dcs-section-title">{contactSectionTitle}</div>
                <div className="dcs-section-body">
                  <div className="dcs-stack">
                    <div className="dcs-auth-top">
                      {!userData?.clientId ? (
                        <div className="dcs-auth-shell">
                          <div className="dcs-auth-right">
                            <div
                              className={`dcs-auth-link ${
                                authMode === "guest" ? "dcs-auth-active" : ""
                              }`}
                              onClick={() => setAuthMode("guest")}
                            >
                              Guest
                            </div>
                            <div
                              className={`dcs-auth-link ${
                                authMode === "login" ? "dcs-auth-active" : ""
                              }`}
                              onClick={() => setAuthMode("login")}
                            >
                              Sign in
                            </div>
                            <div
                              className={`dcs-auth-link ${
                                authMode === "register" ? "dcs-auth-active" : ""
                              }`}
                              onClick={() => setAuthMode("register")}
                            >
                              Register
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <p className="dcs-soft-note dcs-auth-copy">
                        To maximize your benefits, we&apos;ll check if you already have an
                        account with this phone number and continue your order in one step.
                      </p>
                    </div>

                    {userData?.clientId ? (
                      <div className="dcs-contact-summary">
                        <div className="dcs-contact-summary-main">
                          <div className="dcs-contact-avatar">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 21C20 17.6863 16.4183 15 12 15C7.58172 15 4 17.6863 4 21"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <circle
                                cx="12"
                                cy="8"
                                r="4"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                          <div>
                            <div className="dcs-contact-summary-name">
                              {`${dcsSafe(preferredProfileData?.firstName)} ${dcsSafe(
                                preferredProfileData?.lastName
                              )}`.trim()}
                            </div>
                            <div className="dcs-contact-summary-line">
                              {formatPhoneDisplay(preferredProfileData?.phoneNo)}
                            </div>
                            <div className="dcs-contact-summary-line">
                              {dcsSafe(preferredProfileData?.email)}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="dcs-signout-link"
                          onClick={handleCheckoutSignOut}
                        >
                          Sign out
                        </button>
                      </div>
                    ) : null}

                    <>
                        {userData?.clientId ? null : authMode === "login" ? (
                          <div className="dcs-login-grid">
                            <div className="dcs-compact-field">
                              <label className="dcs-field-label">Email</label>
                              <Input
                                className="dcs-compact-field"
                                size="small"
                                placeholder="Email"
                                value={data.email}
                                sx={{ width: "100%" }}
                                onValueChange={(email) =>
                                  setData((e) => ({ ...e, email }))
                                }
                              />
                            </div>

                            <div className="dcs-password-verify">
                              <div className="dcs-compact-field">
                                <label className="dcs-field-label">Password</label>
                                <Input
                                  className="dcs-compact-field"
                                  size="small"
                                  type="password"
                                  placeholder="Password"
                                  value={data.password}
                                  sx={{ width: "100%" }}
                                  onValueChange={(password) =>
                                    setData((e) => ({ ...e, password }))
                                  }
                                />
                              </div>
                              <Button
                                className="dcs-verify-btn"
                                disabled={loading || !data.email || !data.password}
                                onClick={() =>
                                  loginUser({
                                    emailId: data.email,
                                    password: data.password,
                                  })
                                }
                              >
                                {isLoginVerified ? "Verified" : "Verify details"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                        <div className="dcs-contact-grid">
                          <div className="dcs-phone-field">
                            <label className="dcs-phone-label">Phone Number</label>
                            <div className="dcs-phone-combo">
                              <select className="dcs-country-select" value="+1" disabled>
                                <option value="+1">+1 (CA)</option>
                              </select>
                              <input
                                className="dcs-phone-input"
                                value={data.phoneNo}
                                placeholder="Phone number"
                                onChange={(e) =>
                                  setData((prev) => ({
                                    ...prev,
                                    phoneNo: String(e.target.value || "")
                                      .replace(/\D/g, "")
                                      .slice(0, 10),
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="dcs-compact-field">
                            <label className="dcs-field-label">Email</label>
                            <Input
                              className="dcs-compact-field"
                              size="small"
                              placeholder="Email"
                              value={data.email}
                              sx={{ width: "100%" }}
                              onValueChange={(email) => setData((e) => ({ ...e, email }))}
                            />
                          </div>
                        </div>

                        <div className="dcs-two-col">
                          <div className="dcs-compact-field">
                            <label className="dcs-field-label">First Name</label>
                            <Input
                              className="dcs-compact-field"
                              size="small"
                              placeholder="First Name"
                              value={data.firstName}
                              sx={{ width: "100%" }}
                              onValueChange={(firstName) =>
                                setData((e) => ({ ...e, firstName }))
                              }
                            />
                          </div>
                          <div className="dcs-compact-field">
                            <label className="dcs-field-label">Last Name</label>
                            <Input
                              className="dcs-compact-field"
                              size="small"
                              placeholder="Last Name"
                              value={data.lastName}
                              sx={{ width: "100%" }}
                              onValueChange={(lastName) =>
                                setData((e) => ({ ...e, lastName }))
                              }
                            />
                          </div>
                        </div>
                        {authMode === "register" ? (
                          <div className="dcs-stack">
                            <div className="dcs-two-col">
                              <div className="dcs-compact-field">
                                <label className="dcs-field-label">Password</label>
                                <Input
                                  className="dcs-compact-field"
                                  size="small"
                                  type="password"
                                  placeholder="Password"
                                  value={data.password}
                                  sx={{ width: "100%" }}
                                  onValueChange={(password) =>
                                    setData((e) => ({ ...e, password }))
                                  }
                                />
                              </div>
                              <div className="dcs-compact-field">
                                <label className="dcs-field-label">Confirm Password</label>
                                <Input
                                  className="dcs-compact-field"
                                  size="small"
                                  type="password"
                                  placeholder="Confirm Password"
                                  value={data.confirmPassword}
                                  sx={{ width: "100%" }}
                                  onValueChange={(confirmPassword) =>
                                    setData((e) => ({ ...e, confirmPassword }))
                                  }
                                />
                              </div>
                            </div>
                            <Button
                              className="dcs-verify-btn"
                              disabled={
                                loading ||
                                !data.phoneNo ||
                                !data.email ||
                                !data.firstName ||
                                !data.lastName ||
                                !data.password ||
                                !data.confirmPassword
                              }
                              onClick={verifyOrRegisterUser}
                            >
                              Create account
                            </Button>
                          </div>
                        ) : null}
                          </>
                        )}
                    </>

                    <div className="mt-6">
                      <p className="text-[16px] font-[600] text-[#101828]">Payment method</p>
                      <div className="dcs-payment-grid mt-3">
                        {paymentOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className={`dcs-payment-card ${
                              selectedPayment === option.id ? "dcs-payment-card-active" : ""
                            }`}
                            onClick={() => setSelectedPayment(option.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="shrink-0">{option.icon}</div>
                              <div>
                                <div className="text-[14px] font-[600]">{option.label}</div>
                                <div className="mt-0.5 text-[12px] leading-[1.35] opacity-80">
                                  {option.description}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {selectedPaymentOption && (
                      <div className="mt-4 rounded-2xl border border-black/10 bg-white px-4 py-3 text-[13px] text-[#475467]">
                        Selected payment:{" "}
                        <span className="font-[600] text-[#101828]">
                          {selectedPaymentOption.label}
                        </span>
                      </div>
                    )}

                    <div className="mt-6">
                      <div className="dcs-order-notes-card">
                        <TextArea
                          label="Order Notes"
                          rows={3}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Add any special instructions..."
                        />
                      </div>
                    </div>

                  </div>
                </div>
              </div>
                </div>

                <div className="dcs-side-stack">
                <div className="dcs-side-card dcs-side-card-sticky">
                  <div className="dcs-summary-row !pt-0">
                    <p className="text-[14px] font-[600] text-[#475467]">SubTotal</p>
                    <p className="text-[14px] font-[600] text-[#101828]">
                      CA${Number(checkoutData?.finalBill?.[0]?.amt || 0).toFixed(2)}
                    </p>
                  </div>

                  {checkoutData?.finalBill?.slice(1).map((item: any) =>
                    item.amt > 0 ? (
                      <div key={item.title} className="dcs-summary-row">
                        <p className="text-[14px] font-[600] text-[#475467]">{item.title}</p>
                        <p className="text-[14px] font-[600] text-[#101828]">
                          CA${Number(item.amt || 0).toFixed(2)}
                        </p>
                      </div>
                    ) : null
                  )}

                  {checkoutData?.orderType === ORDER.DELIVERY && !!deliveryCharge && (
                    <div className="dcs-summary-row">
                      <p className="text-[14px] font-[600] text-[#475467]">Delivery Charge</p>
                      <p className="text-[14px] font-[600] text-[#101828]">
                        CA${deliveryCharge.toFixed(2)}
                      </p>
                    </div>
                  )}

                  <div className="mt-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[18px] font-[600] text-[#101828]">Tip the staff</p>
                      <p className="text-[18px] font-[600] text-[#101828]">
                        CA${tip.toFixed(2)}
                      </p>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button className="!rounded-full" onClick={() => updateTip(tip10)}>
                        10%
                      </Button>
                      <Button className="!rounded-full" onClick={() => updateTip(tip15)}>
                        15%
                      </Button>
                      <Button className="!rounded-full" onClick={() => updateTip(tip20)}>
                        20%
                      </Button>
                      <Button className="!rounded-full" onClick={focusCustomTipInput}>
                        Other
                      </Button>
                    </div>

                    <div className="mt-3">
                      <Input
                        size="small"
                        value={tip}
                        inputRef={customTipInputRef}
                        sx={{ width: "100%" }}
                        onValueChange={(t) => +t >= 0 && updateTip(+t)}
                      />
                    </div>
                  </div>

                  <div className="dcs-total-box">
                    <p className="text-[18px] font-[600] text-[#101828]">Total</p>
                    <p className="text-[34px] font-[600] text-[#101828]">
                      CA${((checkoutData?.total || 0) + tip + deliveryCharge).toFixed(2)}
                    </p>
                  </div>

                </div>

                  {!!cart?.length && (
                    <div className="dcs-side-section">
                      <div className="dcs-order-summary">
                        <div className="dcs-order-summary-header">
                          <div className="dcs-order-summary-scroll-title">Order summary</div>
                          <div className="dcs-order-summary-count">
                            {orderSummaryQty} item{orderSummaryQty === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="dcs-order-summary-list">
                          {cart.map((item: any, index: number) => {
                            const itemImage = getCartItemImage(item);
                            const itemMeta = getOrderSummaryMeta(item);
                            const itemName =
                              item?.fullDescription ||
                              item?.productName ||
                              item?.name ||
                              `Item ${index + 1}`;
                            const itemQty = Number(item?.quantity ?? 1) || 1;

                            return (
                              <div key={`${itemName}-${index}`} className="dcs-order-summary-row">
                                <div className="dcs-order-summary-qty">{itemQty}x</div>
                                {itemImage ? (
                                  <img
                                    src={itemImage}
                                    alt={itemName}
                                    className="dcs-order-summary-thumb"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="dcs-order-summary-thumb dcs-order-summary-thumb-fallback">
                                    No Image
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <div className="dcs-order-summary-name">{itemName}</div>
                                  {itemMeta ? (
                                    <div className="dcs-order-summary-meta">{itemMeta}</div>
                                  ) : null}
                                </div>
                                <div className="dcs-order-summary-price">
                                  ${Number(item?.subTotal || 0).toFixed(2)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Dialog
            open={isScheduleDialogOpen}
            onClose={() => setIsScheduleDialogOpen(false)}
            className="dcs-slot-dialog"
          >
            <div className="dcs-slot-shell">
              <div className="dcs-slot-head">
                <div>
                  <h3 className="dcs-slot-title">Schedule your order</h3>
                  <p className="dcs-slot-copy">
                    Choose a {activeOrderType === ORDER.DELIVERY ? "delivery" : "pickup"}{" "}
                    date and an available 15-minute time slot.
                  </p>
                </div>

                <IconButton
                  className="dcs-slot-close"
                  onClick={() => setIsScheduleDialogOpen(false)}
                >
                  <CloseIcon />
                </IconButton>
              </div>

              <div className="mt-4">
                <p className="text-[15px] font-[700] text-[#101828]">Select a date</p>
                <div
                  ref={slotDayRowRef}
                  className="dcs-slot-day-row"
                  onWheel={handleSlotDayWheel}
                  onMouseDown={handleSlotDayMouseDown}
                  onMouseMove={handleSlotDayMouseMove}
                  onMouseUp={stopSlotDayDrag}
                  onMouseLeave={stopSlotDayDrag}
                >
                  {availableDateOptions.map((option) => (
                    <button
                      key={option.value}
                      ref={(node) => {
                        slotDayButtonRefs.current[option.value] = node;
                      }}
                      type="button"
                      disabled={option.disabled}
                      className={`dcs-slot-day ${
                        slotPickerDate === option.value ? "dcs-slot-day-active" : ""
                      } ${option.disabled ? "dcs-slot-day-disabled" : ""}`}
                      onClick={() => {
                        if (option.disabled) return;
                        setSlotPickerDate(option.value);
                      }}
                    >
                      <div className="dcs-slot-day-label">{option.dayLabel}</div>
                      <div className="dcs-slot-date-label">{option.dateLabel}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 border-t border-black/10 pt-4">
                <p className="text-[15px] font-[700] text-[#101828]">
                  Select a {activeOrderType === ORDER.DELIVERY ? "delivery" : "pickup"}{" "}
                  time
                </p>
                <p className="mt-1 text-[12px] text-[#667085]">
                  Only valid available slots are shown.
                </p>

                {availableSlots.length ? (
                  <div className="dcs-slot-grid">
                    {availableSlots.map((slot) => (
                      <button
                        key={`${slotPickerDate}-${slot.value}`}
                        type="button"
                        className={`dcs-slot-btn ${
                          data.onDate === slotPickerDate && data.onTime === slot.value
                            ? "dcs-slot-btn-active"
                            : ""
                        }`}
                        onClick={() => {
                          const nextSchedule = {
                            onDate: slotPickerDate,
                            onTime: slot.value,
                          };
                          setData((prev) => ({
                            ...prev,
                            ...nextSchedule,
                          }));
                          dispatch(updateCheckoutData(nextSchedule));
                          setIsScheduleDialogOpen(false);
                        }}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl bg-[rgba(16,24,40,.04)] px-4 py-5 text-[13px] text-[#667085]">
                    No slots available for this date.
                  </div>
                )}
              </div>
            </div>
          </Dialog>

          <Dialog
            open={isPaymentDialogOpen}
            onClose={() => setIsPaymentDialogOpen(false)}
            maxWidth={false}
            PaperProps={{
              sx: {
                width: "min(960px, calc(100vw - 32px))",
                maxWidth: "960px",
                borderRadius: "24px",
                overflow: "hidden",
                margin: "16px",
              },
            }}
          >
            <div className="w-full max-h-[min(88vh,820px)] overflow-auto bg-white">
              <PaymentPage
                setStage={setStage}
                embedded
                onClose={() => setIsPaymentDialogOpen(false)}
              />
            </div>
          </Dialog>

          <div className="dcs-footer">
            <div className="dcs-footer-inner">
              <div className="dcs-footer-spacer" />
              <div>
                {timeError ? (
                  <div className="mb-3">
                    <p className="rounded-2xl border border-red-200 bg-red-50/90 px-4 py-3 text-center text-sm font-[600] text-red-500 shadow-sm">
                      {timeError}
                    </p>
                  </div>
                ) : (
                  isRestDate && (
                    <div className="mb-3">
                      <p className="rounded-2xl border border-red-200 bg-red-50/90 px-4 py-3 text-center text-sm font-[600] text-red-500 shadow-sm">
                        Order placing is off please try again later
                      </p>
                    </div>
                  )
                )}

                <div className="dcs-continue-wrap">
                  <Button
                    className="dcs-continue-btn"
                    onClick={handleSubmit}
                    disabled={loading || !!timeError || isRestDate || !selectedPaymentOption}
                  >
                    {!selectedPaymentOption
                      ? "Select Payment Method"
                      : selectedPaymentOption.kind === "counter"
                      ? activeOrderType === ORDER.DELIVERY
                        ? "Place Delivery Order"
                        : "Place Pickup Order"
                      : `Continue with ${selectedPaymentOption.label}`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Details;
